/**
 * FocalPointEditor — click/drag a pin on the original image to set the subject's
 * focal point. Renders live overlay boxes showing where the 3 crop variants
 * (4:5, 1:1, 9:16) will land BEFORE the user clicks "Neu zuschneiden".
 *
 * Props:
 *   media:    full media doc (must contain id, focal_point, content_type, original_filename)
 *   thumbUrl: blob URL of the original image (already loaded by parent)
 *   onSaved:  callback({focal_point, focal_point_source, variants}) after successful PUT
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Crosshair, Sparkles, Save, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Preview overlay presets — keep in sync with CROP_PRESETS in server.py
const PREVIEW_PRESETS = [
  { key: "instagram_feed_4_5", ratio: 4 / 5,  color: "#2563EB", label: "4:5" },
  { key: "instagram_post",     ratio: 1 / 1,  color: "#3B82F6", label: "1:1" },
  { key: "instagram_story",    ratio: 9 / 16, color: "#10B981", label: "9:16" },
];

// Same math as backend smart_crop_to_aspect — returns crop rect as %-of-image.
// `point` is normalized focal point in image coords (0..1).
function computeCropBox(naturalW, naturalH, targetRatio, point) {
  const srcRatio = naturalW / naturalH;
  if (srcRatio > targetRatio) {
    // source wider — full height, crop sides
    const newWpx = naturalH * targetRatio;
    const ideal = naturalW * point.x - newWpx / 2;
    const left = Math.max(0, Math.min(naturalW - newWpx, ideal));
    return { left: (left / naturalW) * 100, top: 0, width: (newWpx / naturalW) * 100, height: 100 };
  }
  if (srcRatio < targetRatio) {
    // source taller — full width, crop top/bottom
    const newHpx = naturalW / targetRatio;
    const ideal = naturalH * point.y - newHpx / 2;
    const top = Math.max(0, Math.min(naturalH - newHpx, ideal));
    return { left: 0, top: (top / naturalH) * 100, width: 100, height: (newHpx / naturalH) * 100 };
  }
  return { left: 0, top: 0, width: 100, height: 100 };
}

export default function FocalPointEditor({ media, thumbUrl, onSaved }) {
  const initial = media?.focal_point || { x: 0.5, y: 0.5 };
  const [point, setPoint] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [dims, setDims] = useState(null); // { w, h } natural image dims
  const [showOverlays, setShowOverlays] = useState(true);
  const [hiddenPresets, setHiddenPresets] = useState({}); // key -> bool
  const imgWrapRef = useRef(null);

  useEffect(() => {
    setPoint(media?.focal_point || { x: 0.5, y: 0.5 });
  }, [media?.id, media?.focal_point]);

  const dirty = Math.abs(point.x - initial.x) > 0.005 || Math.abs(point.y - initial.y) > 0.005;
  const source = media?.focal_point_source || (media?.focal_point ? "ai" : "default");

  const updateFromEvent = (e) => {
    const wrap = imgWrapRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setPoint({
      x: Math.max(0, Math.min(1, x)),
      y: Math.max(0, Math.min(1, y)),
    });
  };

  const onMouseDown = (e) => {
    e.preventDefault();
    updateFromEvent(e);
    const onMove = (ev) => updateFromEvent(ev);
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const onTouchStart = (e) => {
    if (!e.touches?.[0]) return;
    updateFromEvent(e.touches[0]);
  };
  const onTouchMove = (e) => {
    if (!e.touches?.[0]) return;
    e.preventDefault();
    updateFromEvent(e.touches[0]);
  };

  const reset = () => setPoint({ x: 0.5, y: 0.5 });

  const save = async () => {
    if (!media?.id) return;
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/media/${media.id}/focal-point`,
        { x: point.x, y: point.y },
        { withCredentials: true },
      );
      toast.success("Motiv-Punkt gesetzt — Varianten neu erzeugt");
      onSaved?.({
        focal_point: res.data.focal_point,
        focal_point_source: res.data.focal_point_source,
        variants: res.data.variants,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    }
    setSaving(false);
  };

  if (!thumbUrl) {
    return (
      <div className="text-xs text-[var(--studio-text-muted)] italic">
        Originalbild wird geladen…
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="focal-point-editor">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Crosshair className="w-4 h-4 text-[#2563EB]" />
            <p className="text-sm font-semibold">Motiv-Punkt + Live-Crop-Vorschau</p>
            {source === "ai" && (
              <span
                className="text-[10px] bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded font-mono flex items-center gap-1"
                data-testid="focal-source-ai"
              >
                <Sparkles className="w-2.5 h-2.5" /> AI
              </span>
            )}
            {source === "manual" && (
              <span
                className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-mono"
                data-testid="focal-source-manual"
              >
                manuell
              </span>
            )}
          </div>
          <p className="text-xs text-[var(--studio-text-muted)] mt-1">
            Klicke oder ziehe den roten Pin auf das wichtigste Motiv. Die farbigen Rahmen zeigen live, was in jedem Crop-Format landet.
          </p>
          <p
            className="text-[10px] text-[var(--studio-text-muted)] font-mono mt-1"
            data-testid="focal-point-coords"
          >
            x={point.x.toFixed(2)} · y={point.y.toFixed(2)}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowOverlays((s) => !s)}
            className="h-8 text-xs"
            data-testid="toggle-overlays-btn"
            title={showOverlays ? "Rahmen ausblenden" : "Rahmen einblenden"}
          >
            {showOverlays ? <EyeOff className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
            Rahmen
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={reset}
            disabled={saving}
            className="h-8 text-xs"
            data-testid="focal-reset-btn"
          >
            Mitte
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={saving || !dirty}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-8 text-xs disabled:opacity-50"
            data-testid="focal-save-btn"
          >
            {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
            Neu zuschneiden
          </Button>
        </div>
      </div>

      {/* Legend */}
      {showOverlays && (
        <div className="flex items-center gap-3 text-[11px] flex-wrap" data-testid="overlay-legend">
          {PREVIEW_PRESETS.map((p) => {
            const hidden = !!hiddenPresets[p.key];
            return (
              <button
                key={p.key}
                onClick={() => setHiddenPresets((h) => ({ ...h, [p.key]: !h[p.key] }))}
                className={`flex items-center gap-1.5 rounded px-2 py-0.5 border transition ${
                  hidden
                    ? "opacity-40 border-transparent"
                    : "border-[var(--studio-border)] bg-white"
                }`}
                data-testid={`legend-toggle-${p.key}`}
                title={hidden ? "Einblenden" : "Ausblenden"}
              >
                <span
                  className="w-3 h-3 rounded-sm border-2"
                  style={{ borderColor: p.color }}
                />
                <span className="font-mono">{p.label}</span>
              </button>
            );
          })}
        </div>
      )}

      <div
        ref={imgWrapRef}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        className="relative rounded-lg overflow-hidden border border-[var(--studio-border)] bg-[var(--studio-bg)] cursor-crosshair select-none touch-none mx-auto"
        style={dims ? { aspectRatio: `${dims.w}/${dims.h}`, maxHeight: 420, maxWidth: dims.w / dims.h > 1 ? "100%" : 420 * (dims.w / dims.h) } : { maxHeight: 360 }}
        data-testid="focal-point-canvas"
      >
        <img
          src={thumbUrl}
          alt={media?.original_filename || "Original"}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          draggable={false}
          onLoad={(e) => setDims({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
        />

        {/* Live crop overlays */}
        {showOverlays && dims && PREVIEW_PRESETS.map((p) => {
          if (hiddenPresets[p.key]) return null;
          const box = computeCropBox(dims.w, dims.h, p.ratio, point);
          return (
            <div
              key={p.key}
              className="absolute pointer-events-none transition-all duration-100"
              style={{
                left: `${box.left}%`,
                top: `${box.top}%`,
                width: `${box.width}%`,
                height: `${box.height}%`,
                border: `2px solid ${p.color}`,
                boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.6)`,
              }}
              data-testid={`crop-overlay-${p.key}`}
            >
              <span
                className="absolute -top-0.5 left-1 text-[9px] font-mono font-bold px-1 rounded-sm leading-tight"
                style={{ background: p.color, color: "white" }}
              >
                {p.label}
              </span>
            </div>
          );
        })}

        {/* Pin (always on top) */}
        <div
          className="absolute pointer-events-none z-10"
          style={{
            left: `${point.x * 100}%`,
            top: `${point.y * 100}%`,
            transform: "translate(-50%, -50%)",
          }}
          data-testid="focal-point-pin"
        >
          <div className="w-6 h-6 rounded-full border-[3px] border-[#2563EB] bg-[#2563EB]/30 shadow-[0_0_0_2px_rgba(255,255,255,0.85)]" />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-white" />
        </div>

        {/* Crosshair guides */}
        <div
          className="absolute top-0 bottom-0 w-px bg-[#2563EB]/40 pointer-events-none"
          style={{ left: `${point.x * 100}%` }}
        />
        <div
          className="absolute left-0 right-0 h-px bg-[#2563EB]/40 pointer-events-none"
          style={{ top: `${point.y * 100}%` }}
        />
      </div>
    </div>
  );
}
